import React, { useMemo } from "react";
import { prepareOAuthHandoff } from "../../../common/utils/oauth_handoff";
import { oauthCallbackUrl } from "../../../common/utils/oauth_callback";
import { GoogleIcon } from "../../../common/assets/icons";

/**
 * The one place this app hands a value of its own choosing to Google's OAuth.
 *
 * `state` travels to accounts.google.com and back, so whatever is in it lands in Google's request
 * logs and in the browser history of a foreign origin. It now carries a nonce and nothing else: the
 * return address — which on the share routes IS the capability — waits on this origin, and the nonce
 * is what lets the return be recognised as this browser's own flow rather than somebody else's code.
 */
export const getOAuthGmailUrl = state => {
	const GM_AUTH_URI = "https://accounts.google.com/o/oauth2/auth";
	const GM_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
	const GM_SCOPES = [
		"https://www.googleapis.com/auth/userinfo.email",
		"https://www.googleapis.com/auth/userinfo.profile",
	];

	const params = {
		redirect_uri: oauthCallbackUrl(),
		response_type: "code",
		client_id: GM_CLIENT_ID,
		scope: GM_SCOPES.join(" "),
		state,
	};

	const url = new URL(GM_AUTH_URI);
	url.search = new URLSearchParams(params).toString();

	return url;
};

const OAuthGoogleButton = ({ buttonText, redirectPath }) => {
	// The `state` has to be in the href, so it is built on render — but the address is written to
	// origin storage only when the user actually sets off, or a login page that merely rendered
	// would leave a share token sitting there until it expired.
	const handoff = useMemo(() => prepareOAuthHandoff(redirectPath), [redirectPath]);
	const link = getOAuthGmailUrl(handoff.state)?.href;

	// Three events, because "setting off" is not only a left click. Middle-click dispatches
	// `auxclick` and never `click`, and the context menu's "Open link in new tab" dispatches neither
	// — so with `onClick` alone the handoff was never written, and the perfectly legitimate return
	// that followed was refused as a flow this browser did not start. `contextmenu` fires when the
	// menu opens rather than when the item is chosen, which writes a little early; the record is
	// per-origin and expires in ten minutes, and an unused one is inert because its nonce never
	// left the browser.
	return (
		<a
			href={link}
			onClick={handoff.remember}
			onAuxClick={handoff.remember}
			onContextMenu={handoff.remember}
			className="flex justify-center items-center gap-[15px] bg-white dark:bg-dark-gray-raised rounded-[10px] h-[54px] w-full mt-2 text-text-primary dark:text-text-dark-gray text-[20px] font-semibold leading-[140%] transition-all duration-200 select-none cursor-pointer [box-shadow:0px_2px_8px_0px_rgba(0,0,0,0.1)] hover:[box-shadow:rgba(0,0,0,0.24)_0px_3px_8px]"
			data-testid="oauth-google-button"
		>
			<GoogleIcon /> {buttonText}
		</a>
	);
};

export default OAuthGoogleButton;
